import { ref } from 'vue'
import axiosInstance from '@/axios'

/**
 * useStorefrontCategory -- fetch one storefront category page
 * (`GET /api/storefront/<slug>/category[/<categorySlug>]`).
 *
 * Card: Live Storefront - Home/Category/Product (ADR 0060/0061)
 * https://trello.com/c/q7EQ3a0b -- pebble P5.
 *
 * An empty/root categorySlug resolves the HOME category (the endpoint's
 * empty-slug semantics). A HIDDEN category resolves 200 with hidden=true --
 * the caller renders a "not in the store menu" notice but still renders the
 * page in full (never 404). Unknown/cross-store slug -> 404.
 *
 * Payload (from _category_body):
 *   { name, slug, hidden, is_home, banner_image, rich_text, products: [...] }
 * rich_text is nh3-sanitized at write time; the caller re-sanitizes at the
 * v-html sink anyway (defense-in-depth, same as the campaign message sink).
 * products are ordered by StoreCategoryProduct.sort_order -- rendered verbatim.
 *
 * Public surface:
 *   const { category, loading, error, fetchCategory } = useStorefrontCategory()
 *   category: ref<Object|null>  last successful payload
 *   loading:  ref<boolean>
 *   error:    ref<{status,message}|null>
 *   fetchCategory(slug, categorySlug): Promise<void>
 *
 * Behavior mirrors useStorefrontProduct: concurrent fetches are de-duped via
 * a generation counter (late stale responses discarded); `category` is
 * cleared synchronously on a new fetch so a stale payload never bleeds across
 * slug changes; a category with no products AND no banner/rich_text is a VALID
 * sparse page (not an error).
 */
export function useStorefrontCategory() {
    const category = ref(null)
    const loading = ref(false)
    const error = ref(null)

    let generation = 0
    let currentAbort = null

    const fetchCategory = async (slug, categorySlug = '') => {
        if (slug === undefined || slug === null || slug === '') {
            error.value = { status: 0, message: 'Missing store identifier' }
            category.value = null
            loading.value = false
            return
        }

        if (currentAbort) {
            currentAbort.abort()
        }

        const myGeneration = ++generation
        const controller =
            typeof AbortController !== 'undefined'
                ? new AbortController()
                : null
        currentAbort = controller

        category.value = null
        loading.value = true
        error.value = null

        // Empty categorySlug -> the bare /category path (home category). A
        // trailing slash is fine (the endpoint registers /category and
        // /category/); build the cleanest path per case.
        const base = `/api/storefront/${encodeURIComponent(slug)}/category`
        const url =
            categorySlug === undefined ||
            categorySlug === null ||
            categorySlug === ''
                ? base
                : `${base}/${encodeURIComponent(categorySlug)}`

        try {
            const response = await axiosInstance.get(url, {
                ...(controller ? { signal: controller.signal } : {}),
            })

            if (myGeneration !== generation) return

            category.value = response.data || null
            error.value = null
        } catch (err) {
            if (myGeneration !== generation) return

            const isAbort =
                err?.name === 'CanceledError' ||
                err?.name === 'AbortError' ||
                err?.code === 'ERR_CANCELED'
            if (isAbort) return

            const status = err?.response?.status ?? 0
            error.value = mapCategoryError(status)
            category.value = null
        } finally {
            if (myGeneration === generation) {
                loading.value = false
                currentAbort = null
            }
        }
    }

    return { category, loading, error, fetchCategory }
}

function mapCategoryError(status) {
    if (status === 404) {
        return { status: 404, message: 'This page is not available.' }
    }
    return { status, message: 'Failed to load this page. Please try again.' }
}
